import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useClearFixtureStatusOverride,
  useLiveKillSwitch,
  useOptaLiveFixtures,
  useSetFixtureLifecycleStatus,
  useSetLiveKillSwitch,
} from '../api/hooks';
import {
  effectiveFixtureStatus,
  formatApiError,
  LifecycleButtons,
  statusBadge,
  WriterFanoutBanner,
} from './liveFixtureLifecycleUi';
import { liveFeedLabel, liveFeedSource, liveVendorId } from './liveFeedUi';
import {
  ALL_LEAGUES,
  filterAndSortFixtures,
  leagueOptionsFromFixtures,
  LIVE_SORT_OPTIONS,
  SORT_KEY_RELEVANCE,
} from './liveFixturesFilters';
import './ActiveFixturesPage.css';
import './LivePage.css';

function formatStartTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

export default function LiveFixturesPage() {
  const [fanoutWriters, setFanoutWriters] = useState(true);
  const [liveCoveredOnly, setLiveCoveredOnly] = useState(false);
  const [league, setLeague] = useState(ALL_LEAGUES);
  const [sortKey, setSortKey] = useState(SORT_KEY_RELEVANCE);
  const [sortDir, setSortDir] = useState('asc');
  const [lastResult, setLastResult] = useState(null);
  const [lastError, setLastError] = useState(null);

  const { data: killData, isLoading: killLoading } = useLiveKillSwitch();
  const killOn = Boolean(killData?.live_kill_switch);
  const setKillMut = useSetLiveKillSwitch();

  const { data, isLoading, error, refetch, dataUpdatedAt } = useOptaLiveFixtures(
    undefined,
    {},
    liveCoveredOnly
  );
  const fixtures = useMemo(() => data?.fixtures ?? [], [data?.fixtures]);
  const liveCoveredCount = useMemo(
    () => fixtures.filter((f) => f.is_live_covered).length,
    [fixtures]
  );

  const setStatusMut = useSetFixtureLifecycleStatus();
  const clearOverrideMut = useClearFixtureStatusOverride();

  const busy =
    setKillMut.isPending || setStatusMut.isPending || clearOverrideMut.isPending;

  const toggleKill = async () => {
    setLastError(null);
    setLastResult(null);
    try {
      const res = await setKillMut.mutateAsync({
        enabled: !killOn,
        fanout_writers: fanoutWriters,
      });
      setLastResult(res);
    } catch (e) {
      setLastError(formatApiError(e));
    }
  };

  const applyStatus = async (gameId, status) => {
    if (!status) return;
    setLastError(null);
    setLastResult(null);
    try {
      const res = await setStatusMut.mutateAsync({
        gameId,
        status,
        reason: 'live_dashboard',
        fanout_writers: fanoutWriters,
      });
      setLastResult(res);
    } catch (e) {
      setLastError(formatApiError(e));
    }
  };

  const clearOverride = async (gameId) => {
    setLastError(null);
    setLastResult(null);
    try {
      const res = await clearOverrideMut.mutateAsync({
        gameId,
        fanoutWriters,
      });
      setLastResult(res);
    } catch (e) {
      setLastError(formatApiError(e));
    }
  };

  // Options come from the unfiltered set so picking a league never empties the dropdown.
  const leagueOptions = useMemo(() => leagueOptionsFromFixtures(fixtures), [fixtures]);

  // A league that vanishes from the window (fixtures rolled off) would otherwise leave the
  // table permanently empty with no hint why.
  useEffect(() => {
    if (league !== ALL_LEAGUES && !leagueOptions.includes(league)) setLeague(ALL_LEAGUES);
  }, [league, leagueOptions]);

  const rows = useMemo(
    () =>
      filterAndSortFixtures(fixtures, { league, sortKey, sortDir }).map((f) => {
        const gameId = f.game_id || f.fixture_id;
        return { ...f, gameId };
      }),
    [fixtures, league, sortKey, sortDir]
  );

  if (isLoading && !data) {
    return (
      <div className="live-page active-fixtures-page">
        <div className="loading">Loading fixtures…</div>
      </div>
    );
  }

  return (
    <div className="live-page active-fixtures-page">
      <div className="live-kill-banner">
        <div className="live-kill-copy">
          <h2 className="live-kill-title">Live kill switch</h2>
          <p className="live-subtitle">
            When on: no in-play markets, live-covered fixtures auto-complete after kickoff.
            Persists to DB and syncs all configured writers (same fan-out as batch suspension).
          </p>
          {killOn && (
            <p className="live-kill-active-label">KILL SWITCH IS ACTIVE</p>
          )}
        </div>
        <div className="live-kill-actions">
          <label className="live-kill-fanout-check">
            <input
              type="checkbox"
              checked={fanoutWriters}
              onChange={(e) => setFanoutWriters(e.target.checked)}
              disabled={busy}
            />
            <span>Sync writers after change</span>
          </label>
          <button
            type="button"
            className={`live-kill-btn${killOn ? ' live-kill-btn--on' : ''}`}
            onClick={toggleKill}
            disabled={busy || killLoading}
          >
            {setKillMut.isPending
              ? 'Updating…'
              : killOn
                ? 'LIVE KILL SWITCH ON — click to disable'
                : 'ACTIVATE LIVE KILL SWITCH'}
          </button>
        </div>
      </div>

      <div className="page-header">
        <div>
          <h1>Live</h1>
          <p className="live-subtitle">
            One click per state sets the lifecycle on any fixture from yesterday through two days ahead in
            America/New_York calendar days (scheduled / live / suspended / complete) —
            including leagues not in <code>in_game_offered_events</code>. Use this for early
            complete or reopen when kickoff moves. Live event dashboards remain under
            live-covered rows.
          </p>
        </div>
        {dataUpdatedAt > 0 && (
          <div className="last-update">
            Updated: {new Date(dataUpdatedAt).toLocaleTimeString()}
          </div>
        )}
        <button type="button" className="button" onClick={() => refetch()} disabled={busy}>
          Refresh
        </button>
      </div>

      <div className="live-filters">
        <label className="live-filter live-filter-toggle">
          <span className="live-filter-label">Live-covered</span>
          <button
            type="button"
            className={`live-toggle${liveCoveredOnly ? ' on' : ''}`}
            onClick={() => setLiveCoveredOnly((v) => !v)}
            aria-pressed={liveCoveredOnly}
            disabled={busy}
          >
            <span className="live-toggle-knob" />
            <span className="live-toggle-text">
              {liveCoveredOnly ? 'Covered only' : 'All fixtures'}
            </span>
          </button>
        </label>
        <label className="live-filter">
          <span className="live-filter-label">League</span>
          <select value={league} onChange={(e) => setLeague(e.target.value)}>
            <option value={ALL_LEAGUES}>All leagues</option>
            {leagueOptions.map((lg) => (
              <option key={lg} value={lg}>
                {lg}
              </option>
            ))}
          </select>
        </label>
        <label className="live-filter">
          <span className="live-filter-label">Sort by</span>
          <select value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
            {LIVE_SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="live-filter live-filter-toggle">
          <span className="live-filter-label">Start time</span>
          <button
            type="button"
            className="live-btn live-sort-dir"
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            title={
              sortKey === SORT_KEY_RELEVANCE
                ? 'Reverses start time within each relevance group'
                : 'Reverses the start-time order'
            }
          >
            {sortDir === 'asc' ? '\u2191 Earliest first' : '\u2193 Latest first'}
          </button>
        </label>
        {(league !== ALL_LEAGUES
          || sortKey !== SORT_KEY_RELEVANCE
          || sortDir !== 'asc'
          || liveCoveredOnly) && (
          <button
            type="button"
            className="live-btn live-filter-clear"
            onClick={() => {
              setLeague(ALL_LEAGUES);
              setSortKey(SORT_KEY_RELEVANCE);
              setSortDir('asc');
              setLiveCoveredOnly(false);
            }}
          >
            Clear filters
          </button>
        )}
        <span className="live-filter-count">
          {rows.length}
          {rows.length === fixtures.length ? '' : ` of ${fixtures.length}`} shown
        </span>
      </div>

      {error && (
        <div className="error-message">
          Failed to load fixtures: {error.message || 'unknown error'}
        </div>
      )}
      {lastError && <div className="error-message">{lastError}</div>}
      <WriterFanoutBanner result={lastResult} />

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{data?.count ?? fixtures.length}</div>
          <div className="stat-label">{liveCoveredOnly ? 'Live covered' : 'In window'}</div>
        </div>
        {!liveCoveredOnly && (
          <div className="stat-card">
            <div className="stat-value">{liveCoveredCount}</div>
            <div className="stat-label">Live covered</div>
          </div>
        )}
        <div className="stat-card">
          <div className="stat-value">{killOn ? 'ON' : 'off'}</div>
          <div className="stat-label">Kill switch</div>
        </div>
      </div>

      <div className="card live-section">
        <h2 className="card-title">Fixtures</h2>
        {rows.length === 0 ? (
          <p className="no-data">
            {fixtures.length > 0 && league !== ALL_LEAGUES ? (
              <>No fixtures in {league} — {fixtures.length} in other leagues.</>
            ) : (
              <>
                No fixtures in the retention/lookahead window
                {liveCoveredOnly ? ' with is_live_covered.' : '.'}
              </>
            )}
          </p>
        ) : (
          <div className="live-table-wrap">
            <table className="data-table live-fixtures-table">
              <thead>
                <tr>
                  <th>Match</th>
                  <th>Sport</th>
                  <th>League</th>
                  <th>Start (UTC)</th>
                  <th>Status</th>
                  <th className="live-lifecycle-col">Lifecycle</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => {
                  const { gameId } = f;
                  const label = [f.home_team, f.away_team].filter(Boolean).join(' vs ') || gameId;
                  const draft = effectiveFixtureStatus(f);
                  const hasOverride = Boolean(f.status_override);
                  const covered = Boolean(f.is_live_covered);
                  const feedLabel = liveFeedLabel(liveFeedSource(null, f));
                  const vendorId = liveVendorId(f, { feed_source: liveFeedSource(null, f) });
                  const detailTo = covered
                    ? `/live/${encodeURIComponent(gameId)}`
                    : `/game/${encodeURIComponent(gameId)}`;
                  return (
                    <tr key={gameId || f.opta_id || f.sportradar_game_id}>
                      <td>
                        <Link
                          className="live-fixture-link"
                          to={detailTo}
                          state={
                            covered
                              ? { optaId: f.opta_id, sportradarGameId: f.sportradar_game_id }
                              : undefined
                          }
                        >
                          {label}
                        </Link>
                        {covered && (
                          <span
                            className="live-covered-tag"
                            title={vendorId ? `${feedLabel} ${vendorId}` : `no ${feedLabel} ID`}
                          >
                            live
                          </span>
                        )}
                      </td>
                      <td className="live-muted">{f.sport || '—'}</td>
                      <td>{f.league || '—'}</td>
                      <td>{formatStartTime(f.start_time_utc)}</td>
                      <td>
                        {statusBadge(f)}
                        {hasOverride && (
                          <span className="live-override-tag" title={f.status_override_reason || ''}>
                            locked
                          </span>
                        )}
                      </td>
                      <td className="live-lifecycle-col">
                        <LifecycleButtons
                          current={draft}
                          busy={busy}
                          hasOverride={hasOverride}
                          size="sm"
                          onSelect={(status) => applyStatus(gameId, status)}
                          onClearOverride={() => clearOverride(gameId)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
